import { Omega } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { isValidUnit, suggestUnits, unitIndex, unitTokenAt, type UnitSuggestion } from './unit-utils';
import { useDelayedStickyError } from './use-settled-value';

import { TextField } from '@/components/ui/text-field';

/**
 * A unit string: a picker for the characters a keyboard cannot reach, and a list of the units that
 * exist.
 *
 * WHY NOT a prop on TextField. `variant='literal'` is semantic, not cosmetic — the component
 * defines it as "matched against a student answer" — and its other users are MCQ options, keywords,
 * ordering items, FITB words and variable names, where a units picker would be nonsense. A prop
 * would be opt-in and technically fine, but the symbol list would then be repeated at every unit
 * call site and free to drift. One component owns it once.
 *
 * WHY IT IS USED ON THE STUDENT'S BOX TOO, not only the teacher's editor: a student typing `Ω` has
 * exactly the same keyboard problem, and the same trouble knowing which spellings exist. Grading
 * compares strings, so a student who cannot produce the string cannot produce the answer.
 */

/**
 * Only characters with no keyboard route. `%` is Shift+5 and `-` is a key, so neither appears here
 * even though both are accepted by the parser — offering them would pad the list with things a
 * teacher can already type, which is the fastest way to make a picker not worth opening.
 *
 * `⁻¹` is one entry rather than a bare `⁻`: the sign alone is only meaningful followed by a digit,
 * and `s⁻¹` is what people actually write.
 *
 * Every entry must PARSE — see `normalizeSymbols` in unit-utils, which maps each to the ASCII form
 * mathjs wants. A picker that inserts text the field then marks "Unrecognized" is worse than no
 * picker, so the two lists are a matched pair and must be changed together.
 */
const UNIT_SYMBOLS: Array<{ char: string; key: string }> = [
  { char: 'µ',  key: 'micro' },
  { char: '°',  key: 'degree' },
  { char: 'Ω',  key: 'ohm' },
  { char: 'Å',  key: 'angstrom' },
  { char: '²',  key: 'squared' },
  { char: '³',  key: 'cubed' },
  { char: '⁻¹', key: 'inverse' },
  { char: '·',  key: 'times' },
  { char: '‰',  key: 'permille' },
  // The astronomical bodies. `M⊙` is how a solar mass is properly written, and without these it
  // would be a spelling the field accepts and nobody can produce — the suggestion list cannot
  // offer it either, since typing `Msun` matches the row `Msun`, not the row `M⊙`.
  { char: '⊙',  key: 'sun' },
  { char: '⊕',  key: 'earth' },
  { char: '♃',  key: 'jupiter' },
];

/** Two rows of five at 32px plus gaps and padding — comfortably inside a phone-width panel,
 *  where the single row this replaced was ~330px and would not fit. */
const MENU_ESTIMATED_WIDTH = 188;

/** How many units the suggestion list offers at once. Past about this many the list stops being
 *  read and starts being scrolled, which is a different and worse interaction. */
const SUGGESTION_LIMIT = 8;

type Anchored = { top: number; left: number; width?: number } | null;

/**
 * Where a popover of this size goes, given the field it belongs to.
 *
 * Pure, and separate from the effect that calls it, because the rule and its TIMING failed
 * independently: the rule was right from the start, and the effect stopped re-running the moment
 * the popover's own height changed — so a list that flipped above the field while it had eight rows
 * kept that position when it shrank to one, and floated. Placement has to be recomputed from the
 * height it has NOW, every time that height can have changed.
 */
export function placeAnchored(
  anchor: { top: number; bottom: number; left: number; right: number },
  box: { width: number; height: number },
  viewport: { width: number; height: number },
  preferredLeft: number,
  margin = 8,
): { top: number; left: number } {
  const left = Math.max(margin, Math.min(preferredLeft, viewport.width - box.width - margin));
  // Below when it fits, which keeps the list in reading order under the field. Above only when it
  // does not — a field low in a long editor panel has nothing underneath it — and then its BOTTOM
  // edge sits against the field, so the two stay touching whatever the height.
  const fitsBelow = anchor.bottom + 4 + box.height <= viewport.height - margin;
  const top = fitsBelow ? anchor.bottom + 4 : Math.max(margin, anchor.top - box.height - 4);
  return { top, left };
}

/**
 * Places a popover under an anchor and keeps it on screen.
 *
 * Shared by the symbol menu and the suggestion list because getting it wrong is invisible until it
 * is on a phone: a field low in a long editor panel has nothing underneath it, and a menu wider
 * than the distance to the left edge runs off. Measured in a LAYOUT effect so the correction lands
 * in the same paint as the first render.
 */
function useAnchoredPosition(open: boolean, anchor: React.RefObject<HTMLElement | null>,
                             menu: React.RefObject<HTMLElement | null>, estimatedWidth: number,
                             matchAnchorWidth = false, contentKey: string | number = '') {
  const [pos, setPos] = useState<Anchored>(null);
  const [tick, setTick] = useState(0);

  /**
   * Re-measure while the page moves under it.
   *
   * `position: fixed` is measured against the viewport, so a popover placed once and left alone
   * stays where the field USED to be the moment anything scrolls — and the editor panel this sits
   * in is a scroller of its own, which is why the listener is capturing: a scroll inside an element
   * does not bubble.
   */
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const remeasure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTick(t => t + 1));
    };
    window.addEventListener('scroll', remeasure, true);
    window.addEventListener('resize', remeasure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', remeasure, true);
      window.removeEventListener('resize', remeasure);
    };
  }, [open]);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return;
    // Scrolled out of sight: drop the position rather than clamping, which would peg the popover to
    // the edge of the screen and leave it floating over whatever the field has been replaced by. It
    // comes back, still open, when the field does.
    if (rect.bottom < 0 || rect.top > window.innerHeight) { setPos(null); return; }
    setPos(matchAnchorWidth
      ? { top: rect.bottom + 4, left: rect.left, width: rect.width }
      : { top: rect.bottom + 4, left: rect.right - estimatedWidth });
  }, [open, tick, anchor, estimatedWidth, matchAnchorWidth]);

  /**
   * `contentKey` is in the dependencies because the popover's HEIGHT is what this reads, and
   * nothing else here changes when the number of rows does — the anchor has not moved, the window
   * has not scrolled, and `pos` is exactly what it was. Without it the placement is whatever the
   * list needed when it opened.
   */
  useLayoutEffect(() => {
    if (!open || !pos) return;
    const el = menu.current;
    const rect = anchor.current?.getBoundingClientRect();
    if (!el || !rect) return;
    const box = el.getBoundingClientRect();
    const placed = placeAnchored(rect, box, { width: window.innerWidth, height: window.innerHeight },
      matchAnchorWidth ? rect.left : rect.right - estimatedWidth);
    if (Math.abs(placed.left - pos.left) > 0.5 || Math.abs(placed.top - pos.top) > 0.5) {
      setPos({ ...pos, ...placed });
    }
  }, [open, pos, contentKey, anchor, menu, estimatedWidth, matchAnchorWidth]);

  return pos;
}

export function UnitField({ value, onChange, ariaLabel, hasError, className, onFocus, inputRef, strict, fieldId }: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  /** Names the data key this field writes, so an undo can point at it. */
  fieldId?: string;
  hasError?: boolean;
  className?: string;
  /** Releases the shared rich-text toolbar — a unit is parsed by mathjs and must stay plain. */
  onFocus?: () => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  /**
   * Only a unit that PARSES may leave this field.
   *
   * For the student's box. An unrecognised unit is not a wrong answer to be graded, it is text the
   * block cannot reason about at all — so rather than accepting it and marking it incorrect, the
   * field keeps the last value that parsed and discards the rest on the way out. What the student
   * submits is then always something the block understands, and a student who cannot produce one
   * lands on Incomplete Answer, which says what actually happened.
   *
   * Off for the teacher's fields, deliberately: an author needs to be TOLD their unit is
   * unrecognised, and silently reverting a canonical unit they pasted explains nothing. Their
   * fields flag it inline instead, and that flag gates publication.
   */
  strict?: boolean;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [caret, setCaret] = useState(0);
  /**
   * Under `strict` the field holds its own text, because `value` is only allowed to move when it
   * parses: typing `mg/` has to be possible on the way to `mg/L` without the parent ever seeing it.
   * Unstrict, there is no draft at all and `value` is the single source, exactly as before.
   */
  const [draft, setDraft] = useState(value);
  const text = strict ? draft : value;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const firstRef = useRef<HTMLButtonElement | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  // An external change — the test bar reset after a submission, a block loaded — is authoritative.
  useEffect(() => { setDraft(value); }, [value]);

  /**
   * Build the suggestion index after paint, not on the first keystroke.
   *
   * It is built lazily and cached forever, so SOMEONE pays for it once — and until this ran it was
   * whoever typed the first character, who then watched the list arrive late. Idle time is free
   * here and imperceptible; the work is the same either way, it just happens where nobody is
   * waiting on it.
   */
  useEffect(() => {
    const idle = typeof requestIdleCallback === 'function';
    const id = idle ? requestIdleCallback(() => void unitIndex()) : window.setTimeout(() => void unitIndex(), 0);
    return () => { if (idle) cancelIdleCallback(id); else clearTimeout(id); };
  }, []);

  const menuPos = useAnchoredPosition(menuOpen, triggerRef, menuRef, MENU_ESTIMATED_WIDTH);

  const [tokenStart, tokenEnd] = unitTokenAt(text, caret);
  const token = text.slice(tokenStart, tokenEnd);
  const suggestions = listOpen ? suggestUnits(token, SUGGESTION_LIMIT) : [];
  /*
   * Only worth saying when they have typed something, and NEVER when the field as a whole parses.
   *
   * This list describes the TOKEN under the caret, and `unitTokenAt('m/s^2', 5)` returns the
   * exponent digit `2`, which matches nothing in the index. So every compound unit ending in an
   * exponent was told "Unit not found" at the moment it was finished and correct. A teacher read
   * that as a rejection and authored `m/s/s` as the canonical instead, that being the one spelling
   * of acceleration whose last token is a real unit; their students were then marked wrong for
   * writing `m/s²`. One mis-scoped label, two steps from a grading defect.
   *
   * The field already has two honest error channels, `hasError` and the block's issue banner, and
   * both describe the whole value. A search result does not get to contradict them.
   */
  const notFound = listOpen && token.trim() !== '' && suggestions.length === 0 && !isValidUnit(text);
  // Placed AFTER the rows it measures, so the key names the height about to be rendered rather than
  // the last one. `-1` for the not-found panel, which is one line of its own rather than zero rows.
  const listPos = useAnchoredPosition(listOpen, wrapRef, listRef, 0, true,
    notFound ? -1 : suggestions.length);

  useEffect(() => { setActive(0); }, [token]);

  useEffect(() => {
    if (!menuOpen) return;
    requestAnimationFrame(() => { firstRef.current?.focus(); });
    function onOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)
        && triggerRef.current && !triggerRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [menuOpen]);

  /** Commits when the text parses, which under `strict` is the only way `value` ever moves. */
  function write(next: string, nextCaret = next.length) {
    setDraft(next);
    setCaret(nextCaret);
    if (!strict || next === '' || isValidUnit(next)) onChange(next);
  }

  function handleChange(next: string) {
    write(next, fieldRef.current?.selectionStart ?? next.length);
    setListOpen(true);
  }

  /**
   * Replaces the TOKEN the caret sits in, not the whole field. A compound is several units in one
   * string, so choosing `L` while standing after `mg/` has to leave the `mg/` alone — which is what
   * makes a compound composable from the list rather than only typeable from memory.
   */
  function choose(entry: UnitSuggestion) {
    const next = text.slice(0, tokenStart) + entry.label + text.slice(tokenEnd);
    const at = tokenStart + entry.label.length;
    write(next, at);
    setListOpen(false);
    requestAnimationFrame(() => {
      fieldRef.current?.focus();
      fieldRef.current?.setSelectionRange(at, at);
    });
  }

  /**
   * Inserted at the CARET, not appended. A unit is often built around a symbol rather than ended by
   * one — `mg·L⁻¹` needs the dot in the middle — and appending would make the picker useless for
   * exactly the compound units it exists to help with.
   */
  function insert(char: string) {
    const el = fieldRef.current;
    const at = el?.selectionStart ?? text.length;
    const to = el?.selectionEnd ?? text.length;
    const next = text.slice(0, at) + char + text.slice(to);
    write(next, at + char.length);
    setMenuOpen(false);
    // Inserting a symbol IS typing, and it is the moment the list is most useful: `µ` on its own
    // is not a unit, and what the picker is really being used to reach is `µm` or `µmol`.
    setListOpen(true);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + char.length, at + char.length);
    });
  }

  function onFieldKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!listOpen) { setListOpen(true); return; }
      if (suggestions.length === 0) return;
      e.preventDefault();
      setActive(i => (i + (e.key === 'ArrowDown' ? 1 : suggestions.length - 1)) % suggestions.length);
      return;
    }
    if (e.key === 'Enter' && listOpen && suggestions[active]) {
      // Only when the list is open with something highlighted. Otherwise Enter belongs to whatever
      // encloses the field — the test bar submits on it.
      e.preventDefault();
      choose(suggestions[active]!);
      return;
    }
    if (e.key === 'Escape' && listOpen) { e.stopPropagation(); setListOpen(false); return; }
    if (e.key === 'Tab') setListOpen(false);
  }

  /**
   * The gate, and the only place it fires. Nothing is prevented while typing — every unit passes
   * through unparseable states on the way in — so the check is on the way OUT, where the text either
   * is a unit or is discarded back to the last one that was.
   */
  function onFieldBlur() {
    setListOpen(false);
    if (strict && draft !== value && draft !== '' && !isValidUnit(draft)) setDraft(value);
  }

  // ONE border, owned by the wrapper, exactly as FormulaField frames its own contentEditable and
  // variables trigger. Two bordered halves welded with a negative margin still read as two things
  // touching, and they cannot even agree on when to turn red: TextField holds a new error back
  // until typing settles (`useDelayedStickyError`), so a button reading `hasError` raw lit up
  // first. With a single border there is one thing to colour and nothing to keep in step.
  const showError = useDelayedStickyError(!!hasError, text);
  const activeId = suggestions.length ? `${listId}-${active}` : undefined;

  return (
    <div
      ref={wrapRef}
      data-field-id={fieldId}
      /* The bordered box for the accessibility panel's ring. It matters more here than anywhere
         else: this container is `overflow-hidden`, so an outline drawn on the combobox inside it or
         on the unit-symbol button beside it is CLIPPED, and showed as a line between the two rather
         than a ring around anything. */
      data-field-box=''
      className={`relative flex items-stretch overflow-hidden rounded-md border transition-colors ${
        showError ? 'border-ws-error-fg' : 'border-ws-border'
      } ${className ?? ''}`}
    >
      <TextField
        variant='unit'
        seamless
        value={text}
        onChange={handleChange}
        ariaLabel={ariaLabel}
        // Focus alone opens nothing. `suggestUnits` returns nothing for an empty token either, so
        // this is belt and braces — but the two say different things, and only this one says it
        // about the INTERACTION: the list is a response to typing, not to arriving.
        onFocus={() => onFocus?.()}
        onBlur={onFieldBlur}
        onKeyDown={onFieldKeyDown}
        combobox={{ expanded: listOpen && (suggestions.length > 0 || notFound), listId, activeId }}
        className='min-w-0 flex-1 bg-ws-btn'
        inputRef={el => {
          fieldRef.current = el;
          inputRef?.(el);
        }}
      />
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setMenuOpen(v => !v)}
        aria-label={t('blocks.units.symbolPicker')}
        aria-haspopup='menu'
        aria-expanded={menuOpen}
        className='flex shrink-0 items-center self-stretch border-l border-ws-border bg-ws-primary/10 px-2 text-ws-primary transition-colors hover:bg-ws-primary/20 dark:bg-ws-text-link/10 dark:text-ws-text-link dark:hover:bg-ws-text-link/20'
      >
        <Omega aria-hidden='true' className='h-3.5 w-3.5' />
      </button>

      {/* The result count, for a screen reader that cannot see the list appear. */}
      <span role='status' aria-live='polite' className='sr-only'>
        {listOpen && token.trim()
          ? (notFound ? t('blocks.units.notFound') : t('blocks.units.matchCount', { count: suggestions.length }))
          : ''}
      </span>

      {listOpen && listPos && (suggestions.length > 0 || notFound) && createPortal(
        <div
          ref={listRef}
          role='listbox'
          // Focus never comes here — the combobox pattern keeps it in the input and points at the
          // highlighted row with `aria-activedescendant` — but an interactive role has to be
          // reachable programmatically, and -1 keeps it out of the Tab order.
          tabIndex={-1}
          id={listId}
          aria-label={t('blocks.units.suggestionList')}
          style={{ position: 'fixed', top: listPos.top, left: listPos.left, minWidth: listPos.width, zIndex: 9999 }}
          // Focus must not leave the input, or the field would blur and the gate would fire before
          // the click ever lands.
          onMouseDown={e => e.preventDefault()}
          // `w-max` so the box is as wide as its widest row: capping a scroll container at the
          // field's width made the rows scroll SIDEWAYS, and a list you have to scroll horizontally
          // to read is not a list. The anchor width is the floor, not the ceiling, and the viewport
          // cap keeps a long compound from running off the edge.
          className='max-h-64 w-max max-w-[90vw] overflow-y-auto rounded-lg border border-ws-border bg-ws-elevated py-1 shadow-lg'
        >
          {notFound ? (
            <p className='px-3 py-2 text-xs italic text-ws-muted'>{t('blocks.units.notFound')}</p>
          ) : suggestions.map((s, i) => (
            <button
              key={s.label}
              id={`${listId}-${i}`}
              type='button'
              role='option'
              aria-selected={i === active}
              onClick={() => choose(s)}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
                i === active ? 'bg-ws-hover' : ''
              }`}
            >
              <span className='whitespace-nowrap text-ws-text'>{s.label}</span>
              {/* The shortest form, and only when it is a different string — a row must not tell
                  you that `g` is another way of writing `g`. Nothing else goes here: a quantity
                  beside each row would hint at what kind of answer is wanted. */}
              {s.primary !== s.label && (
                <span className='ml-auto shrink-0 whitespace-nowrap text-xs text-ws-muted'>{s.primary}</span>
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}

      {menuOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          role='menu'
          // Focusable so the interactive role is reachable programmatically; -1 keeps it out of the
          // Tab order, since focus is handed straight to the first item when the menu opens.
          tabIndex={-1}
          aria-label={t('blocks.units.symbolPicker')}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className='grid grid-cols-5 gap-1 rounded-lg border border-ws-border bg-ws-elevated p-1.5 shadow-lg'
          onKeyDown={e => {
            if (e.key === 'Escape') { e.stopPropagation(); setMenuOpen(false); triggerRef.current?.focus(); return; }
            // Same reason as FormulaField's picker: this is a document.body portal, so native Tab
            // traversal can eject focus out of the page rather than moving to the next control a
            // sighted user expects. Closing and refocusing the trigger keeps Tab predictable.
            if (e.key === 'Tab') { e.preventDefault(); setMenuOpen(false); triggerRef.current?.focus(); return; }
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
              const idx = items.indexOf(document.activeElement as HTMLElement);
              const next = e.key === 'ArrowRight'
                ? items[(idx + 1) % items.length]
                : items[(idx - 1 + items.length) % items.length];
              next?.focus();
            }
          }}
        >
          {UNIT_SYMBOLS.map((sym, i) => (
            <button
              key={sym.char}
              ref={i === 0 ? firstRef : undefined}
              type='button'
              role='menuitem'
              onClick={() => insert(sym.char)}
              // The character alone is not a label a screen reader can read out — "µ" is announced
              // inconsistently and "·" often not at all — so each carries its name.
              aria-label={t(`blocks.units.symbol.${sym.key}`)}
              title={t(`blocks.units.symbol.${sym.key}`)}
              // Sans and a size up: these glyphs are the whole content of the button, and several of them
              // (`·`, `°`, the superscripts) are small marks that need the room.
              className='flex h-9 w-9 items-center justify-center rounded text-base text-ws-text transition-colors hover:bg-ws-hover focus:bg-ws-hover focus:outline-none'
            >
              <span aria-hidden='true'>{sym.char}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
